// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { WEDDING_CONFIG } from '../config';

const HomePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    attendance: 'yes',
    guests: 1,
    message: '',
  });
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Countdown timer - Fixed for January 10, 2026
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      // Wedding: January 10, 2026 at 2:00 PM
      const weddingDate = new Date(2026, 0, 10, 14, 0, 0).getTime();
      const distance = weddingDate - now;

      if (distance < 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(timer);
      } else {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { submitRSVP } = await import('../services/rsvpService');
      const result = await submitRSVP(formData);

      if (result.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          attendance: 'yes',
          guests: 1,
          message: '',
        });
      } else {
        alert(result.message || 'Failed to submit RSVP');
      }
    } catch (error) {
      console.error('RSVP error:', error);
      alert(error.message || 'Failed to submit RSVP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <Navbar />
      
      {/* Fixed Background Image - ONLY shows for glass sections */}
      <div style={styles.backgroundImage}></div>

      {/* Hero Section - Uses Config Gradient Colors */}
      <section style={styles.hero}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Welcome to Our Wedding</h1>
          <p style={styles.heroSubtitle}>
            Join us as we celebrate the beginning of our forever
          </p>
        </div>
      </section>

      {/* Wedding Video Section - Google Drive with Better Loading */}
      <section style={styles.section}>
        <div style={styles.glassCard}>
          <h2 style={styles.sectionTitle}>A Never Ending Story</h2>
          <div style={styles.videoWrapper}>
            <iframe
              style={styles.videoIframe}
              src="https://drive.google.com/file/d/1wriM1lWXQGbM7rWg1NZr7saNI1y-q05r/preview"
              title="Wedding Same Day Edit Video"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              loading="eager"
            ></iframe>
          </div>
          <p style={styles.videoCaption}>
            "a story beginning to unfold, to have and to hold, the future has been told."
            <br />
          </p>
        </div>
      </section>

      {/* Invitation Text Section - GLASS CARD with couple photo background */}
      <section style={styles.section}>
        <div style={styles.glassCard}>
          <h1 style={styles.coupleTitle}>
            {WEDDING_CONFIG.bride} & {WEDDING_CONFIG.groom}
          </h1>
          <div style={styles.divider}></div>
          <p style={styles.bibleVerse}>
            "{WEDDING_CONFIG.bibleVerse}"
          </p>
          <p style={styles.bibleReference}>- {WEDDING_CONFIG.bibleReference}</p>
        </div>
      </section>

      {/* Countdown Section - GLASS CARD */}
      <section style={styles.section}>
        <div style={styles.glassCard}>
          <h2 style={styles.sectionTitle}>Counting Down to Our Special Day</h2>
          <div style={styles.countdownGrid}>
            <div style={styles.countdownItem}>
              <div style={styles.countdownNumber}>{countdown.days}</div>
              <div style={styles.countdownLabel}>Days</div>
            </div>
            <div style={styles.countdownItem}>
              <div style={styles.countdownNumber}>{countdown.hours}</div>
              <div style={styles.countdownLabel}>Hours</div>
            </div>
            <div style={styles.countdownItem}>
              <div style={styles.countdownNumber}>{countdown.minutes}</div>
              <div style={styles.countdownLabel}>Minutes</div>
            </div>
            <div style={styles.countdownItem}>
              <div style={styles.countdownNumber}>{countdown.seconds}</div>
              <div style={styles.countdownLabel}>Seconds</div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Details - GLASS CARD */}
      <section style={styles.section}>
        <div style={styles.glassCard}>
          <h2 style={styles.sectionTitle}>When & Where</h2>
          
          <div style={styles.eventDetail}>
            <h3 style={styles.eventTitle}>The Wedding</h3>
            <p style={styles.eventText}>January 10, 2026 at 2:00 PM</p>
            <p style={styles.eventText}>Our lady of the Most Holy Rosary Parish</p>
          </div>

          <div style={styles.divider}></div>

          <div style={styles.eventDetail}>
            <h3 style={styles.eventTitle}>Reception</h3>
            <p style={styles.eventText}>Reception to follow at</p>
            <p style={styles.eventText}>Rosario Municipal Covered Court</p>
          </div>

          <button 
            onClick={() => navigate('/venue')}
            style={styles.viewMapButton}
          >
            View Map & Directions
          </button>
        </div>
      </section>

      {/* RSVP Form - GLASS CARD (Email now optional!) */}
      <section style={styles.section}>
        <div style={styles.glassCard}>
          {!submitted ? (
            <>
              <h2 style={styles.sectionTitle}>RSVP</h2>
              <p style={styles.rsvpSubtitle}>
                Please let us know if you can make it to our special day!
              </p>

              <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    style={styles.input}
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Will you attend? *</label>
                  <select
                    name="attendance"
                    value={formData.attendance}
                    onChange={handleInputChange}
                    style={styles.select}
                  >
                    <option value="yes">✓ Joyfully Accept</option>
                    <option value="no">✗ Regretfully Decline</option>
                  </select>
                </div>

                {formData.attendance === 'yes' && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Number of Guests *</label>
                    <input
                      type="number"
                      name="guests"
                      value={formData.guests}
                      onChange={handleInputChange}
                      min="1"
                      max="10"
                      style={styles.input}
                      required
                    />
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Message (Optional)</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    rows="4"
                    placeholder="Share your wishes or dietary requirements..."
                  />
                </div>

                <button
                  type="submit"
                  style={styles.submitButton}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit RSVP'}
                </button>
              </form>
            </>
          ) : (
            <div style={styles.thankYou}>
              <h2 style={styles.thankYouTitle}>Thank You!</h2>
              <p style={styles.thankYouText}>
                Your RSVP has been received. We can't wait to celebrate with you!
              </p>
              <button
                onClick={() => setSubmitted(false)}
                style={styles.submitAnotherButton}
              >
                Submit Another RSVP
              </button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  page: {
    position: 'relative',
    minHeight: '100vh',
  },
  backgroundImage: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${WEDDING_CONFIG.images.couple})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    zIndex: -1,
  },
  hero: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    marginTop: '60px',
    background: `linear-gradient(135deg, ${WEDDING_CONFIG.colors.primary}, ${WEDDING_CONFIG.colors.secondary})`,
    position: 'relative',
    zIndex: 1,
  },
  heroContent: {
    textAlign: 'center',
    maxWidth: '800px',
  },
  heroTitle: {
    fontSize: '5rem',
    fontFamily: "'Great Vibes', 'Playfair Display', serif",
    color: 'white',
    marginBottom: '1.5rem',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
    fontWeight: '700',
  },
  heroSubtitle: {
    fontSize: '1.5rem',
    color: 'white',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.2)',
    lineHeight: '1.6',
    fontWeight: '300',
  },
  section: {
    padding: '4rem 2rem',
    position: 'relative',
  },
  glassCard: {
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(0.5px)',
    WebkitBackdropFilter: 'blur(10px)',
    borderRadius: '30px',
    padding: '2rem',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 16px 64px rgba(0, 0, 0, 0.1)',
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  // Video styles
  videoWrapper: {
    position: 'relative',
    paddingBottom: '56.25%', // 16:9 aspect ratio
    height: 0,
    overflow: 'hidden',
    borderRadius: '20px',
    marginTop: '2rem',
    background: '#000',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  videoIframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '20px',
  },
  videoCaption: {
    marginTop: '1.5rem',
    fontSize: '1.2rem',
    color: 'white',
    textAlign: 'center',
    fontStyle: 'italic',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  coupleTitle: {
    fontSize: '4rem',
    fontFamily: "'Playfair Display', serif",
    color: 'white',
    textAlign: 'center',
    marginBottom: '1rem',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
  },
  divider: {
    width: '100px',
    height: '2px',
    background: 'rgba(255, 255, 255, 0.5)',
    margin: '2rem auto',
  },
  bibleVerse: {
    fontSize: '1.3rem',
    fontStyle: 'italic',
    color: 'white',
    textAlign: 'center',
    lineHeight: '1.8',
    marginBottom: '1rem',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  bibleReference: {
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    fontWeight: '600',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  sectionTitle: {
    fontSize: '2.5rem',
    fontFamily: "'Playfair Display', serif",
    color: 'white',
    textAlign: 'center',
    marginBottom: '2rem',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
  },
  countdownGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1.5rem',
    marginTop: '2rem',
  },
  countdownItem: {
    textAlign: 'center',
  },
  countdownNumber: {
    fontSize: '3.5rem',
    fontWeight: '700',
    color: 'white',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
    marginBottom: '0.5rem',
    lineHeight: '1',
  },
  countdownLabel: {
    fontSize: '1rem',
    color: 'rgba(255, 255, 255, 0.9)',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  eventDetail: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  eventTitle: {
    fontSize: '2rem',
    color: 'white',
    marginBottom: '1rem',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
  },
  eventText: {
    fontSize: '1.2rem',
    color: 'rgba(255, 255, 255, 0.95)',
    marginBottom: '0.5rem',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  viewMapButton: {
    display: 'block',
    margin: '2rem auto 0',
    padding: '1rem 2rem',
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(10px)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '50px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  rsvpSubtitle: {
    fontSize: '1.1rem',
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: '2rem',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  form: {
    marginTop: '2rem',
  },
  formGroup: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '0.5rem',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  input: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '15px',
    color: 'white',
    outline: 'none',
    transition: 'all 0.3s',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '15px',
    color: 'white',
    outline: 'none',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '1rem',
    fontSize: '1rem',
    background: 'rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '15px',
    color: 'white',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  submitButton: {
    width: '100%',
    padding: '1.2rem',
    background: `linear-gradient(135deg, ${WEDDING_CONFIG.colors.primary}, ${WEDDING_CONFIG.colors.secondary})`,
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.3s',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
  },
  thankYou: {
    textAlign: 'center',
    padding: '2rem',
  },
  thankYouTitle: {
    fontSize: '3rem',
    color: 'white',
    marginBottom: '1rem',
    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
  },
  thankYouText: {
    fontSize: '1.2rem',
    color: 'rgba(255, 255, 255, 0.95)',
    marginBottom: '2rem',
    textShadow: '1px 1px 2px rgba(0, 0, 0, 0.3)',
  },
  submitAnotherButton: {
    padding: '1rem 2rem',
    background: 'rgba(255, 255, 255, 0.25)',
    backdropFilter: 'blur(10px)',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '50px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
};

// Mobile Responsive
if (typeof window !== 'undefined' && window.innerWidth <= 768) {
  styles.heroTitle.fontSize = '3rem';
  styles.coupleTitle.fontSize = '2.5rem';
  styles.countdownGrid.gridTemplateColumns = 'repeat(2, 1fr)';
  styles.countdownNumber.fontSize = '2.5rem';
  styles.glassCard.padding = '2rem 1.5rem';
}

export default HomePage;